import React, { useEffect, useMemo, useRef, useState } from 'react';
import WorkForm from './WorkForm';
import ProfileEditModal from '../shared/ProfileModal';
import { useConfirm } from '../../UI/Toast';
import type { WorkCollaborator, WorkSubmitMode, WorkVisibility } from '../../../services/postService';

interface EditableWork {
    id: string | number;
    type: 'video' | 'image';
    videoUrl?: string;
    images?: string[];
    thumbnailUrl: string;
    title: string;
    description: string;
    tags: string[];
    visibility?: WorkVisibility;
    collaborators?: WorkCollaborator[];
}

interface WorkSaveResult {
    postId?: string | number;
}

interface WorkEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    work?: EditableWork;
    onSave: (updatedData: any, submitMode: WorkSubmitMode, currentWork?: EditableWork) => Promise<WorkSaveResult | void>;
    zIndex?: number;
}

const DRAFT_STORAGE_KEY = 'publish_work_draft';
const DRAFT_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

// Build a draft-safe snapshot of form data (excludes File objects which can't be serialized)
const buildDraftSnapshot = (formData: any): any => {
    if (!formData) return null;
    return {
        _savedAt: Date.now(),
        id: '',
        type: formData.workType || 'video',
        videoUrl: formData.videoPreview || '',
        images: formData.imagePreviews || [],
        thumbnailUrl: formData.coverPreview || '',
        title: formData.title || '',
        description: formData.description || '',
        tags: formData.tags || [],
        visibility: formData.visibility,
        collaborators: formData.collaborators || [],
    };
};

const WorkEditModal: React.FC<WorkEditModalProps> = ({ isOpen, onClose, work, onSave, zIndex }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMode, setSubmitMode] = useState<WorkSubmitMode | null>(null);
    const [currentWork, setCurrentWork] = useState<EditableWork | undefined>(work);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const latestFormDataRef = useRef<any>(null);
    const { confirm } = useConfirm();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Restore draft from sessionStorage for create mode (no work prop)
        if (!work) {
            try {
                const savedDraft = sessionStorage.getItem(DRAFT_STORAGE_KEY);
                if (savedDraft) {
                    const draft = JSON.parse(savedDraft);
                    const age = Date.now() - (draft._savedAt || 0);
                    if (age < DRAFT_MAX_AGE_MS) {
                        setCurrentWork(draft);
                        latestFormDataRef.current = null;
                        setHasUnsavedChanges(true);
                        return;
                    } else {
                        // Stale draft — drop it
                        sessionStorage.removeItem(DRAFT_STORAGE_KEY);
                    }
                }
            } catch (e) {
                console.warn('Failed to restore draft:', e);
                try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}
            }
        }

        setCurrentWork(work);
        setHasUnsavedChanges(false);
        latestFormDataRef.current = null;
    }, [isOpen, work]);

    // Clear stored draft when background upload succeeds; keep it on failure so it can be restored
    useEffect(() => {
        const handleCompleted = () => {
            try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}
        };
        window.addEventListener('background_work_upload_completed', handleCompleted);
        return () => window.removeEventListener('background_work_upload_completed', handleCompleted);
    }, []);

    const initialFormData = useMemo(() => {
        if (!currentWork) {
            return undefined;
        }

        return {
            workType: currentWork.type,
            videoUrl: currentWork.videoUrl,
            imageUrls: currentWork.images,
            thumbnailUrl: currentWork.thumbnailUrl,
            title: currentWork.title,
            description: currentWork.description,
            tags: currentWork.tags,
            visibility: currentWork.visibility,
            collaborators: currentWork.collaborators || []
        };
    }, [currentWork]);

    const buildSavedWork = (formData: any, mode: WorkSubmitMode, saveResult?: WorkSaveResult | void): EditableWork | undefined => {
        const postId = saveResult?.postId ?? currentWork?.id;
        if (!postId) {
            return currentWork;
        }

        return {
            id: postId,
            type: formData.workType,
            videoUrl: formData.videoPreview || currentWork?.videoUrl,
            images: formData.workType === 'image'
                ? (formData.imagePreviews?.length ? formData.imagePreviews : currentWork?.images)
                : undefined,
            thumbnailUrl: formData.coverPreview || currentWork?.thumbnailUrl || '',
            title: formData.title,
            description: formData.description,
            tags: formData.tags || [],
            visibility: mode === 'draft' ? 'draft' : 'public',
            collaborators: formData.collaborators || currentWork?.collaborators || []
        };
    };

    const handleSubmit = async (
        formData: any,
        mode: WorkSubmitMode,
        options: { closeAfterSave?: boolean } = {}
    ) => {
        setIsSubmitting(true);
        setSubmitMode(mode);

        // Save snapshot BEFORE attempting submission - ensures recovery on failure
        // (including background upload failures after modal has closed)
        if (!work) {
            try {
                sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(buildDraftSnapshot(formData)));
            } catch (e) { console.warn('Failed to save draft snapshot:', e); }
        }

        try {
            const saveResult = await onSave(formData, mode, currentWork);
            const savedWork = buildSavedWork(formData, mode, saveResult);
            if (savedWork) {
                setCurrentWork(savedWork);
            }
            setHasUnsavedChanges(false);

            // Clear the draft snapshot now that onSave resolved. The
            // background_work_upload_completed event still arms a later
            // cleanup as a safety net, but waiting only for the event
            // leaves a window where the user can reopen the modal and
            // see their just-submitted content "restored" from the draft.
            try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}

            if (mode === 'publish' || options.closeAfterSave) {
                onClose();
            }
        } catch (error) {
            console.error('Failed to save work:', error);
        } finally {
            setIsSubmitting(false);
            setSubmitMode(null);
        }
    };

    const handleRequestClose = async () => {
        if (isSubmitting) {
            return;
        }

        if (!hasUnsavedChanges) {
            onClose();
            return;
        }

        const result = await confirm({
            title: '保存草稿并退出？',
            message: '当前有未保存的修改，是否先保存为草稿再退出？',
            confirmText: '保存并退出',
            cancelText: '直接退出',
            dismissible: false,
            showCloseButton: true
        });

        if (result.action === 'confirm' && latestFormDataRef.current) {
            await handleSubmit(latestFormDataRef.current, 'draft', { closeAfterSave: true });
            return;
        }

        if (result.action === 'cancel') {
            // User explicitly discarded — clear stored draft so it won't re-appear next time
            try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}
            onClose();
        }
    };

    // Based on the `work` prop, not `currentWork`. If no `work` was passed in,
    // we're creating a new post — even if we restored draft data into currentWork.
    const isCreateMode = !work;

    return (
        <ProfileEditModal
            isOpen={isOpen}
            onClose={() => void handleRequestClose()}
            title={isCreateMode ? "发布新作品" : "编辑作品"}
            description={isCreateMode ? "上传您的 AI 创作，与社区分享灵感" : "修改作品的基本信息和发布设置"}
            zIndex={zIndex}
        >
            <WorkForm
                mode={isCreateMode ? 'create' : 'edit'}
                initialData={initialFormData}
                onSubmit={(formData, mode) => handleSubmit(formData, mode)}
                onCancel={() => void handleRequestClose()}
                isSubmitting={isSubmitting}
                submitMode={submitMode}
                onDirtyChange={setHasUnsavedChanges}
                onDataChange={(data) => {
                    latestFormDataRef.current = data;
                }}
            />
        </ProfileEditModal>
    );
};

export default WorkEditModal;
